import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Sparkles, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { quizQuestions } from "@/lib/quizQuestions";
import { trackEvent } from "@/lib/analytics";
import ProtocolResult from "./ProtocolResult";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Step model
//   1..10  = questions
//   11     = profile capture (name, email, phone)
//   12     = loading
//   13     = result
const PROFILE_STEP = 11;
const LOADING_STEP = 12;
const RESULT_STEP = 13;

export default function WellnessQuiz({ t, language }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [protocol, setProtocol] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [error, setError] = useState(false);

  const questions = quizQuestions[language];
  const total = questions.length;

  const resetAll = () => {
    setStep(1);
    setProfile({ name: "", email: "", phone: "" });
    setAnswers({});
    setProtocol(null);
    setLeadId(null);
    setError(false);
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(resetAll, 250);
  };

  const profileValid =
    profile.name.trim().length > 1 &&
    /^\S+@\S+\.\S+$/.test(profile.email) &&
    profile.phone.trim().length >= 6;

  const submit = async () => {
    setStep(LOADING_STEP);
    setError(false);
    try {
      const payload = {
        name: profile.name.trim(),
        email: profile.email.trim(),
        phone: profile.phone.trim(),
        language,
        answers: questions.map((q) => ({
          question_id: q.id,
          question: q.question,
          answer: answers[q.id] || "",
        })),
      };
      const { data } = await axios.post(`${API}/quiz/submit`, payload, {
        timeout: 90000,
      });
      setProtocol(data.protocol);
      setLeadId(data.lead_id);
      setStep(RESULT_STEP);
      trackEvent("quiz_submit", {
        event_category: "conversion",
        language,
        lead_id: data.lead_id,
      });
    } catch (e) {
      console.error(e);
      setError(true);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="open-quiz-btn"
        onClick={() => setOpen(true)}
        className="group inline-flex items-center gap-3 rounded-full bg-[#22c55e] hover:bg-[#16a34a] text-white px-8 py-4 text-sm font-bold tracking-wide transition-all lift shadow-[0_12px_30px_-8px_rgba(34,197,94,0.55)]"
      >
        <Sparkles className="w-4 h-4" />
        {t.quiz.heroCta}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid="quiz-dialog"
          className="max-w-2xl bg-[#f9f6f0] border-[#2c4a3b]/10 rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        >
          <DialogTitle className="sr-only">{t.quiz.heroTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {t.quiz.heroBody}
          </DialogDescription>

          {/* QUESTION STEPS */}
          {step >= 1 && step <= total && (
            <QuestionStep
              t={t}
              index={step}
              total={total}
              question={questions[step - 1]}
              selected={answers[questions[step - 1].id]}
              onSelect={(v) =>
                setAnswers((a) => ({ ...a, [questions[step - 1].id]: v }))
              }
              onBack={() => setStep((s) => Math.max(1, s - 1))}
              onNext={() => setStep((s) => s + 1)}
              isFirst={step === 1}
            />
          )}

          {/* PROFILE STEP (after questions) */}
          {step === PROFILE_STEP && (
            <div className="p-8 md:p-10" data-testid="quiz-profile-step">
              <div className="inline-flex items-center gap-2 bg-[#bc6c25]/12 text-[#9c581e] rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]">
                <Gift className="w-3.5 h-3.5" />
                {t.quiz.heroBadge}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#1a2421] leading-tight">
                {t.quiz.profileTitle}
              </h2>
              <p className="mt-3 text-[#5c6b61]">{t.quiz.profileBody}</p>

              <div className="mt-8 space-y-5">
                <ProfileField
                  label={t.quiz.profileName}
                  value={profile.name}
                  onChange={(v) => setProfile({ ...profile, name: v })}
                  testId="quiz-name"
                />
                <ProfileField
                  label={t.quiz.profileEmail}
                  type="email"
                  value={profile.email}
                  onChange={(v) => setProfile({ ...profile, email: v })}
                  testId="quiz-email"
                />
                <ProfileField
                  label={t.quiz.profilePhone}
                  value={profile.phone}
                  onChange={(v) => setProfile({ ...profile, phone: v })}
                  testId="quiz-phone"
                  placeholder="+52 449 123 4567"
                />
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => setStep(total)}
                  className="text-[#5c6b61] hover:text-[#1a2421] hover:bg-transparent"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.quiz.back}
                </Button>
                <Button
                  data-testid="quiz-submit-btn"
                  disabled={!profileValid}
                  onClick={submit}
                  className="rounded-full bg-[#2c4a3b] hover:bg-[#1e3328] text-[#f9f6f0] px-8 py-6 text-sm font-semibold disabled:opacity-50"
                >
                  {t.quiz.submit}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* LOADING */}
          {step === LOADING_STEP && !error && (
            <div
              className="p-12 md:p-16 flex flex-col items-center text-center"
              data-testid="quiz-loading"
            >
              <Loader2 className="w-10 h-10 animate-spin text-[#2c4a3b]" />
              <h3 className="mt-6 font-serif text-3xl text-[#1a2421]">
                {t.quiz.loadingTitle}
              </h3>
              <p className="mt-3 text-[#5c6b61]">{t.quiz.loadingBody}</p>
            </div>
          )}

          {/* ERROR */}
          {step === LOADING_STEP && error && (
            <div className="p-12 md:p-16 flex flex-col items-center text-center">
              <h3 className="font-serif text-3xl text-[#1a2421]">
                {t.quiz.errorTitle}
              </h3>
              <p className="mt-3 text-[#5c6b61]">{t.quiz.errorBody}</p>
              <Button
                onClick={submit}
                className="mt-6 rounded-full bg-[#2c4a3b] hover:bg-[#1e3328] text-[#f9f6f0] px-8"
              >
                {t.quiz.retry}
              </Button>
            </div>
          )}

          {/* RESULT */}
          {step === RESULT_STEP && protocol && (
            <ProtocolResult
              t={t}
              language={language}
              profile={profile}
              protocol={protocol}
              answers={answers}
              questions={questions}
              leadId={leadId}
              onEmailSent={() => toast.success(t.protocol.emailSent)}
              onEmailError={() => toast.error(t.explorer.error)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProfileField({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5c6b61]">
        {label}
      </Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 bg-white border-[#2c4a3b]/15 rounded-2xl py-6 text-[#1a2421] placeholder:text-[#5c6b61]/50 focus-visible:ring-[#2c4a3b]"
      />
    </div>
  );
}

function QuestionStep({
  t,
  index,
  total,
  question,
  selected,
  onSelect,
  onBack,
  onNext,
  isFirst,
}) {
  return (
    <div className="p-8 md:p-10" data-testid={`quiz-step-${index}`}>
      <div className="flex items-center gap-4">
        <p className="text-overline">{t.quiz.stepOfTotal(index, total)}</p>
        <div className="flex-1 h-[2px] bg-[#2c4a3b]/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#2c4a3b] transition-all duration-500"
            style={{ width: `${(index / total) * 100}%` }}
          />
        </div>
      </div>

      <h2 className="font-serif text-3xl md:text-4xl mt-6 text-[#1a2421] leading-tight">
        {question.question}
      </h2>

      <div className="mt-8 space-y-3">
        {question.options.map((opt, i) => (
          <button
            key={i}
            type="button"
            data-testid={`quiz-opt-${question.id}-${i}`}
            onClick={() => onSelect(opt)}
            className={`w-full text-left rounded-2xl px-5 py-4 border transition-all ${
              selected === opt
                ? "bg-[#2c4a3b] text-[#f9f6f0] border-[#2c4a3b]"
                : "bg-white border-[#2c4a3b]/15 text-[#1a2421] hover:border-[#2c4a3b]/40"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between">
        {!isFirst ? (
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-[#5c6b61] hover:text-[#1a2421] hover:bg-transparent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.quiz.back}
          </Button>
        ) : (
          <span />
        )}
        <Button
          data-testid="quiz-next-btn"
          onClick={onNext}
          disabled={!selected}
          className="rounded-full bg-[#2c4a3b] hover:bg-[#1e3328] text-[#f9f6f0] px-8 py-6 text-sm font-semibold disabled:opacity-50"
        >
          {t.quiz.next}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
